import { isNotNil, toGroupedMap } from '@newfront-insurance/core';
import type { UIMessage } from 'ai';
import { differenceInDays, isToday, isYesterday } from 'date-fns';
import pluralize from 'pluralize';

import type { UIMessageWithMetadata } from '../../../lib/types';
import type { GetChatWithMessagesMessage } from '../../../queries/chats';
import type { GetChatsByFiltersChat } from '../../../queries/chats';

/**
 * Converts backend message format to Vercel AI SDK message format.
 * Vercel AI SDK uses ID, backend uses uuid.
 */
export function backendMessageToSDKMessage(message: GetChatWithMessagesMessage): UIMessageWithMetadata {
  // Extract slash command metadata from backend message metadata
  const backendMetadata = message.metadata as {
    slashCommandUuid?: string;
    slashCommandParameters?: Record<string, string>;
    slashCommandExtraText?: string;
  } | null;

  return {
    ...message,
    id: message.uuid!,
    role: message.role as UIMessage['role'],
    parts: message.parts ? (message.parts as UIMessageWithMetadata['parts']) : [],
    metadata: {
      reaction: message.reaction,
      files:
        message.files?.map((file) => ({
          uuid: file.uuid!,
          filename: file.filename,
        })) ?? [],
      slashCommandUuid: backendMetadata?.slashCommandUuid,
      slashCommandParameters: backendMetadata?.slashCommandParameters,
      slashCommandExtraText: backendMetadata?.slashCommandExtraText,
    },
  };
}

/**
 * Splits chat history results into sections based on their creation date.
 * For today, section title is "Today"
 * For today - 1, section title is "Yesterday"
 * For today - 2, section title is "2 days ago"
 * Up to 7 days ago, section title is "7 days ago"
 * Anything after, section title is "More than a week ago"
 */
export function groupChatsByDate(chats: GetChatsByFiltersChat[]): {
  title: string;
  chats: GetChatsByFiltersChat[];
}[] {
  const today = new Date();
  const formattedChats = chats
    .sort((a, b) => {
      // Order by most recent first
      if (a.lastMessageAt && b.lastMessageAt) {
        return new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime();
      }
      return 0;
    })
    .filter((chat) => isNotNil(chat.lastMessageAt))
    .map((chat) => {
      const date = new Date(chat.lastMessageAt!);
      // If the date is today, return "Today"
      if (isToday(date)) {
        return { groupTitle: 'Today', ...chat };
      }
      // If the date is yesterday, return "Yesterday"
      if (isYesterday(date)) {
        return { groupTitle: 'Yesterday', ...chat };
      }
      // Calculate the number of days ago
      const daysAgo = differenceInDays(today, date);
      // If the date is more than a week ago, return "More than a week ago"
      const groupTitle = daysAgo > 7 ? 'More than a week ago' : `${daysAgo} ${pluralize('day', daysAgo)} ago`;
      return { groupTitle, ...chat };
    });

  const groupedChats = toGroupedMap(formattedChats, (chat) => chat.groupTitle);

  return Array.from(groupedChats.entries()).map(([groupTitle, chatsInGroup]) => ({
    title: groupTitle,
    chats: chatsInGroup,
  }));
}
